import { Box } from "@mui/material";
import React, { useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Unauthorized } from "../../components";
import { CustomTabs } from "../../components/CustomTabs";
import { useCheckAreaAccess } from '../../hooks/useCheckAreaAccess';
import { useCompanySettings } from "../../hooks/useCompanySettings";
import { useIsMobile } from "../../hooks/useIsMobile";
import { useLocalization } from "../../hooks/useLocalization";
import { type TabItem, useTabNavigation } from "../../hooks/useTabNavigation";
import { theme } from "../../theme";
import { EInsightsTabs } from "../../types/NavigationPaths";
import { ExternalUsageTab } from "./ExternalUsageTab/ExternalUsageTab";
import { useHistoricalLicensesRaw } from "./ExternalUsageTab/hooks/useHistoricalLicenses";
import { InternalUsageTab } from "./InternalUsageTab/InternalUsageTab";
import { UserLevelDataTab } from "./UserLevelDataTab/UserLevelDataTab";

export const InsightsPage: React.FC = () => {
  const { t } = useLocalization();
  const { companyId } = useParams();
  const isMobile = useIsMobile();
  const { isUserLevelDataConsentEnabled, isLoading: isSettingsLoading } = useCompanySettings({ companyId });
  const { isLoading: isCheckAccessLoading, access } = useCheckAreaAccess(companyId);

  const areHooksEnabled = !!companyId && !isCheckAccessLoading && access.insights;

  const { licenses: externalLicenses, isLoading: isLicensesLoading } = useHistoricalLicensesRaw(areHooksEnabled);

  const hasExternalLicense = externalLicenses.length > 0;

  const availableTabs = useMemo<TabItem<EInsightsTabs>[]>(() => {
    const tabs = [
      {
        label: t("pages.dataAnalytics.tabs.internalUsage"),
        value: EInsightsTabs.INTERNAL_USAGE,
      },
    ];

    if (hasExternalLicense) {
      tabs.push({
        label: t("pages.dataAnalytics.tabs.externalUsage"),
        value: EInsightsTabs.EXTERNAL_USAGE,
      });
    }

    if (isUserLevelDataConsentEnabled) {
      tabs.push({
        label: t("pages.dataAnalytics.tabs.userLevelData"),
        value: EInsightsTabs.USER_LEVEL_DATA,
      });
    }

    return tabs;
  }, [hasExternalLicense, isUserLevelDataConsentEnabled, t]);

  const { activeTab, handleTabChange } = useTabNavigation<EInsightsTabs>({
    tabEnum: EInsightsTabs,
    defaultTab: EInsightsTabs.INTERNAL_USAGE,
  });

  const isLoading = isSettingsLoading || isLicensesLoading || isCheckAccessLoading;

  const hasTabs = !isMobile || availableTabs.length > 1;

  useEffect(() => {
    if (isLoading) return;
    if (!availableTabs.some((tab) => tab.value === activeTab)) {
      handleTabChange({} as React.SyntheticEvent, EInsightsTabs.INTERNAL_USAGE);
    }
  }, [availableTabs, activeTab, handleTabChange, isLoading]);
  if (isLoading) {
    return null;
  }

  if (!access.insights) {
    return (<Unauthorized />)
  }

  return (
    <Box sx={{ p: 3, pb: 0, pt: 0 }}>
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          bgcolor: theme.colors.defaultBackground,
          marginLeft: -3,
          marginRight: -3,
          px: 3,
        }}
      >
        <CustomTabs
          activeTab={activeTab}
          containerSx={{ paddingTop: { xs: 0, md: 3 }, mb: 0 }}
          onTabChange={handleTabChange}
          tabs={availableTabs}
        />
      </Box>

      {/* Content Rendering */}
      <Box sx={{ paddingTop: 0 }}>
        {activeTab === EInsightsTabs.INTERNAL_USAGE && (
          <InternalUsageTab
            hasTabs={hasTabs}
            isUserLevelDataEnabled={isUserLevelDataConsentEnabled}
          />
        )}
        {activeTab === EInsightsTabs.EXTERNAL_USAGE && (
          <ExternalUsageTab hasTabs={hasTabs} />
        )}
        {activeTab === EInsightsTabs.USER_LEVEL_DATA && (
          <UserLevelDataTab hasTabs={hasTabs} />
        )}
      </Box>
    </Box>
  );
};
